import React, { useState, useCallback } from 'react';
import { PendingSale } from '../types/payment';
import { useDebounce } from '../hooks/useDebounce';

export interface PendingPaymentFilterState {
  globalSearch: string;
  createdBy: string;
  category: string;
  saleState: string;
  status: 'all' | 'PENDING' | 'APPROVED' | 'REJECTED';
  amountExact: string;
  amountMin: string;
  amountMax: string;
  cardBrand: string;
  last4: string;
  dateFrom: string;
  dateTo: string;
}

interface PendingPaymentFiltersProps {
  filters: PendingPaymentFilterState;
  updateFilter: (field: keyof PendingPaymentFilterState, value: string) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  pendingSales: PendingSale[];
  filterStats: {
    total: number;
    filtered: number;
    hidden: number;
  };
}

export const PendingPaymentFilters: React.FC<PendingPaymentFiltersProps> = ({
  filters,
  updateFilter,
  clearFilters,
  hasActiveFilters,
  pendingSales,
  filterStats
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  
  // Debounce the filter updates for better performance
  const debouncedFilters = useDebounce(localFilters, 300);

  // Update parent filters when debounced values change
  React.useEffect(() => {
    Object.entries(debouncedFilters).forEach(([key, value]) => {
      if (filters[key as keyof PendingPaymentFilterState] !== value) {
        updateFilter(key as keyof PendingPaymentFilterState, value);
      }
    });
  }, [debouncedFilters, filters, updateFilter]);

  // Handle local filter changes
  const handleFilterChange = useCallback((field: keyof PendingPaymentFilterState, value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setLocalFilters({
      globalSearch: '',
      createdBy: '',
      category: '',
      saleState: '',
      status: 'all',
      amountExact: '',
      amountMin: '',
      amountMax: '',
      cardBrand: '',
      last4: '',
      dateFrom: '',
      dateTo: ''
    });
    clearFilters();
  }, [clearFilters]);

  // Get unique values for dropdowns
  const uniqueCategories = React.useMemo(() => {
    return Array.from(new Set(pendingSales.map(sale => sale.category).filter(Boolean))).sort() as string[];
  }, [pendingSales]);

  const uniqueSaleStates = React.useMemo(() => {
    return Array.from(new Set(pendingSales.map(sale => sale.sale_state).filter(Boolean))).sort() as string[];
  }, [pendingSales]);

  const uniqueCardBrands = React.useMemo(() => {
    return Array.from(new Set(pendingSales.map(sale => sale.card_brand).filter(Boolean))).sort() as string[];
  }, [pendingSales]);

  const uniqueCreatedBy = React.useMemo(() => {
    return Array.from(new Set(pendingSales.map(sale => sale.created_by).filter(Boolean))).sort() as string[];
  }, [pendingSales]);

  return (
    <div className="pending-payment-filters">
      <div className="filters-header">
        <div className="filters-info">
          <h4>Filter Pending Payments</h4>
          <div className="filter-stats">
            <span className="stats-text">
              Showing <strong>{filterStats.filtered}</strong> of <strong>{filterStats.total}</strong> payments
              {filterStats.hidden > 0 && (
                <span className="hidden-count"> ({filterStats.hidden} filtered out)</span>
              )}
            </span>
          </div>
        </div>
        
        <div className="filters-controls">
          {hasActiveFilters && (
            <button 
              onClick={handleClearFilters}
              className="btn btn-outline-secondary btn-sm"
              title="Clear all filters"
            >
              🗑️ Clear Filters ({Object.values(filters).filter(v => v && v !== 'all').length})
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`btn btn-primary btn-sm ${isExpanded ? 'expanded' : ''}`}
            title={isExpanded ? 'Hide filters' : 'Show filters'}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="filter-icon"
            >
              <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3"/>
            </svg>
            Filters
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="filters-content">
          {/* Global Search */}
          <div className="filter-row">
            <div className="filter-group global-search">
              <label htmlFor="globalSearch">🔍 Global Search</label>
              <input
                id="globalSearch"
                type="text"
                value={localFilters.globalSearch}
                onChange={(e) => handleFilterChange('globalSearch', e.target.value)}
                placeholder="Search across all fields..."
                className="filter-input global-search-input"
              />
              <small className="filter-hint">
                Search transaction ID, customer name, card info, and amounts
              </small>
            </div>
          </div>

          {/* User and Category Filters */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="createdByFilter">Created By</label>
              <select
                id="createdByFilter"
                value={localFilters.createdBy}
                onChange={(e) => handleFilterChange('createdBy', e.target.value)}
                className="filter-select"
              >
                <option value="">All Users</option>
                {uniqueCreatedBy.map(user => (
                  <option key={user} value={user}>
                    {user}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="categoryFilter">Sale Type</label>
              <select
                id="categoryFilter"
                value={localFilters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="filter-select"
              >
                <option value="">All Types</option>
                {uniqueCategories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="saleStateFilter">Sale State</label>
              <select
                id="saleStateFilter"
                value={localFilters.saleState}
                onChange={(e) => handleFilterChange('saleState', e.target.value)}
                className="filter-select"
              >
                <option value="">All States</option>
                {uniqueSaleStates.map(state => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="statusFilter">Status</label>
              <select
                id="statusFilter"
                value={localFilters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>
          </div>

          {/* Amount Filters */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="amountExactFilter">Exact Amount ($)</label>
              <input
                id="amountExactFilter"
                type="number"
                step="0.01"
                min="0"
                value={localFilters.amountExact}
                onChange={(e) => handleFilterChange('amountExact', e.target.value)}
                placeholder="e.g. 100.00"
                className="filter-input"
              />
              <small className="filter-hint">Search for exact amount</small>
            </div>

            <div className="filter-group">
              <label htmlFor="amountMinFilter">Min Amount ($)</label>
              <input
                id="amountMinFilter"
                type="number"
                step="0.01"
                min="0"
                value={localFilters.amountMin}
                onChange={(e) => handleFilterChange('amountMin', e.target.value)}
                placeholder="e.g. 50.00"
                className="filter-input"
              />
              <small className="filter-hint">Greater than or equal to</small>
            </div>

            <div className="filter-group">
              <label htmlFor="amountMaxFilter">Max Amount ($)</label>
              <input
                id="amountMaxFilter"
                type="number"
                step="0.01"
                min="0"
                value={localFilters.amountMax}
                onChange={(e) => handleFilterChange('amountMax', e.target.value)}
                placeholder="e.g. 500.00"
                className="filter-input"
              />
              <small className="filter-hint">Less than or equal to</small>
            </div>
          </div>

          {/* Card and Date Filters */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="cardBrandFilter">Card Brand</label>
              <select
                id="cardBrandFilter"
                value={localFilters.cardBrand}
                onChange={(e) => handleFilterChange('cardBrand', e.target.value)}
                className="filter-select"
              >
                <option value="">All Brands</option>
                {uniqueCardBrands.map(brand => (
                  <option key={brand} value={brand}>
                    {brand.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="last4Filter">Last 4 Digits</label>
              <input
                id="last4Filter"
                type="text"
                maxLength={4}
                value={localFilters.last4}
                onChange={(e) => handleFilterChange('last4', e.target.value.replace(/\D/g, ''))}
                placeholder="1234"
                className="filter-input"
              />
              <small className="filter-hint">Last 4 digits of card</small>
            </div>
          </div>

          {/* Date Range Filters */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="dateFromFilter">Date From</label>
              <input
                id="dateFromFilter"
                type="date"
                value={localFilters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="filter-input date-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="dateToFilter">Date To</label>
              <input
                id="dateToFilter"
                type="date"
                value={localFilters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="filter-input date-input"
              />
            </div>
          </div>

          {/* Filter Tips */}
          <div className="filter-tips">
            <small>
              💡 <strong>Tips:</strong> Use Global Search for quick filtering, or use specific field filters for precise results. 
              Amount filters: use exact for precise matching, or min/max for ranges. All text filters support partial matching (case-insensitive).
            </small>
          </div>
        </div>
      )}

      <style>{`
        .pending-payment-filters {
          background: var(--bg-card);
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--border-primary);
          margin-bottom: 1.5rem;
        }

        .filters-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid var(--border-primary);
        }

        .filters-info h4 {
          margin: 0 0 0.5rem 0;
          color: var(--text-primary);
          font-size: 1.125rem;
          font-weight: 600;
        }

        .filter-stats {
          color: var(--text-secondary);
          font-size: 0.875rem;
        }

        .hidden-count {
          color: var(--warning);
          font-weight: 500;
        }

        .filters-controls {
          display: flex;
          gap: 1rem;
          align-items: center;
        }


        .expanded {
          background: var(--primary-hover) !important;
        }

        .filter-icon {
          transition: transform 0.2s;
        }

        .expanded .filter-icon {
          transform: rotate(180deg);
        }

        .filters-content {
          padding: 1.5rem;
        }

        .filter-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .filter-row:last-of-type {
          margin-bottom: 1rem;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
        }

        .filter-group.global-search {
          grid-column: 1 / -1;
        }

        .filter-group label {
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: var(--text-primary);
          font-size: 0.875rem;
        }

        .filter-input,
        .filter-select {
          padding: 0.75rem;
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 0.875rem;
        }

        .filter-input:focus,
        .filter-select:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px var(--primary-light);
        }

        .global-search-input {
          font-size: 1rem;
          padding: 1rem;
        }

        .filter-hint {
          margin-top: 0.25rem;
          color: var(--text-secondary);
          font-size: 0.75rem;
        }

        .filter-tips {
          background: var(--bg-secondary);
          padding: 1rem;
          border-radius: 4px;
          border-left: 4px solid var(--primary);
        }

        .filter-tips small {
          color: var(--text-secondary);
          line-height: 1.5;
        }

        @media (max-width: 768px) {
          .filters-header {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }

          .filters-controls {
            justify-content: space-between;
          }

          .filter-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};